import { useTranslation } from '@shared/core';
import { BoxProps } from '@withjoy/joykit';
import React from 'react';
import { StyledContainer, StyledFlexItemsContainer, StyledItemContainer, StyledItemIcon, StyledItemTitle, StyledTitle, styles } from './ValuePropsSection.styles';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { Discount, FreeShipping, EasyReturns, PriceMatch, ZeroFee, UniversalRegistry } from './icons';
import { useRegistryOverviewTelemetry } from '@apps/registry/admin/routes/Overview/telemetry';
import { useShopStorefrontContext } from '../../../../components/ShopStorefrontProvider';
import { DelayShip } from '@withjoy/joykit/icons';
import { Heart } from '@assets/icons';
import { REGISTRY_SHIPPING_THRESHOLD } from '@apps/registry/common/util/constants';

interface ValuePropsSectionItemProps {
  title: string;
  icon: JSX.Element;
  link?: string;
  variant?: 'default' | 'overview';
}

const ValuePropsSectionItem: React.FC<ValuePropsSectionItemProps> = ({ title, icon, link, variant = 'default' }) => {
  // Replace hyphens with non-breaking word unicode hyphens
  const hyphenReplacedTitle = title.replace(/-/g, '‑');
  const telemetry = useRegistryOverviewTelemetry();

  const handleOnClick = () => {
    telemetry.registryOverviewInteraction({ component: 'Value Props', method: 'ValuePropClicked', label: title || '' });
    if (link) {
      window.open(link, '_blank');
    }
  };

  return (
    <StyledItemContainer
      {...styles.itemContainer(variant, link)}
      onClick={handleOnClick}
    >
      <StyledItemIcon {...styles.itemIcon}>{icon}</StyledItemIcon>
      <StyledItemTitle {...styles.itemTitle(variant)}>{hyphenReplacedTitle}</StyledItemTitle>
    </StyledItemContainer>
  );
};

export type ValuePropsSectionProps = BoxProps & {
  showTitle?: boolean;
  variant?: 'default' | 'overview';
};

export const ValuePropsSection: React.FC<ValuePropsSectionProps> = ({ showTitle, variant = 'default', ...restProps }) => {
  const { t } = useTranslation('catalogRegistry');
  const { isBabyStorefront, config } = useShopStorefrontContext();
  const translations = t('valuePropsSection');
  const isMobileOrTablet = useIsMobileOrTablet();
  const title = variant === 'overview' ? translations.titleOverview() : translations.title();
  const isOverview = variant === 'overview';
  const showPriceMatch = !isMobileOrTablet || isOverview;
  const valuePropsLinksByStorefront = config.valuePropSectionLinks;

  return (
    <StyledContainer
      {...styles.container(variant)}
      {...restProps}
    >
      {showTitle && <StyledTitle {...styles.title(variant)}>{title}</StyledTitle>}
      <StyledFlexItemsContainer __css={styles.flexItemsContainer(variant)}>
        <ValuePropsSectionItem
          title={translations.freeShipping({ price: REGISTRY_SHIPPING_THRESHOLD })}
          icon={<FreeShipping />}
          link={valuePropsLinksByStorefront.shipping}
          variant={variant}
        />
        <ValuePropsSectionItem
          title={isBabyStorefront ? translations.completionDiscountBaby() : translations.postWeddingDiscount()}
          icon={<Discount />}
          link={valuePropsLinksByStorefront.completionDiscount}
          variant={variant}
        />
        {isOverview && (
          <ValuePropsSectionItem
            title={translations.delayShipping()}
            icon={<DelayShip size={48} />}
            link={valuePropsLinksByStorefront.delayShipping}
            variant={variant}
          />
        )}
        {isOverview && (
          <ValuePropsSectionItem
            title={translations.zeroFeeCashFunds()}
            icon={<ZeroFee />}
            link={valuePropsLinksByStorefront.zeroFeeCashFunds}
            variant={variant}
          />
        )}
        {isOverview && (
          <ValuePropsSectionItem
            title={translations.thankYouNotes()}
            icon={<Heart />}
            link={valuePropsLinksByStorefront.thankYouNotes}
            variant={variant}
          />
        )}

        {isOverview && (
          <ValuePropsSectionItem
            title={translations.universalRegistry()}
            icon={<UniversalRegistry />}
            link={valuePropsLinksByStorefront.universalRegistry}
            variant={variant}
          />
        )}
        <ValuePropsSectionItem
          title={translations.easyReturns()}
          icon={<EasyReturns />}
          link={valuePropsLinksByStorefront.easyReturns}
          variant={variant}
        />
        {showPriceMatch && (
          <ValuePropsSectionItem
            title={translations.priceMatchPromise()}
            icon={<PriceMatch />}
            link={valuePropsLinksByStorefront.priceMatchPromise}
            variant={variant}
          />
        )}
      </StyledFlexItemsContainer>
    </StyledContainer>
  );
};
